if (!customElements.get('videos-carousel')) {
  class VideosCarousel extends HTMLElement {
    connectedCallback() {
      this.swiperEl = this.querySelector('[data-vc-swiper]');
      this.nextBtn = this.querySelector('[data-vc-next]');
      this.counterEl = this.querySelector('[data-vc-counter-current]');
      this.autoplayAllowed =
        this.dataset.autoplay === 'true' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.activeCard = null;
      this.inView = false;

      this.loop = this.fillLoop();
      this.cards = Array.from(this.querySelectorAll('[data-vc-card]'));
      this.bindControls();
      this.observeSelf();
      this.initSwiper();
    }

    disconnectedCallback() {
      clearTimeout(this.swiperRetry);
      if (this.observer) this.observer.disconnect();
      if (this.swiperEl && this.swiperEl.swiper) this.swiperEl.swiper.destroy(true, true);
    }

    /* ── Swiper ─────────────────────────────────────── */

    num(key, fallback) {
      const value = parseFloat(this.dataset[key]);
      return Number.isFinite(value) ? value : fallback;
    }

    fillLoop() {
      const wrapper = this.swiperEl && this.swiperEl.querySelector('.swiper-wrapper');
      if (!wrapper) return false;

      const originals = Array.from(wrapper.children);
      if (originals.length < 2) return false;

      const widest = Math.max(this.num('slidesMobile', 1.7), this.num('slidesTablet', 2.2), this.num('slidesDesktop', 3));
      const needed = 2 * (Math.ceil(widest) + 2);

      while (wrapper.children.length < needed) {
        originals.forEach((slide) => {
          const clone = slide.cloneNode(true);
          clone.removeAttribute('data-shopify-editor-block');
          clone.querySelectorAll('video').forEach((video) => (video.preload = 'none'));
          wrapper.appendChild(clone);
        });
      }

      return true;
    }

    fillOverflow(swiper) {
      if (!swiper.params.loop) return;

      const gap = parseFloat(swiper.params.spaceBetween) || 0;
      const slide = swiper.slidesSizesGrid[0];
      if (!slide) return;

      const perView = Math.ceil(swiper.params.slidesPerView);
      const visible = Math.ceil(
        (document.documentElement.clientWidth - swiper.el.getBoundingClientRect().left + gap) / (slide + gap)
      );
      const looped = Math.max(Math.ceil((visible + 1) / 2), visible - perView + 1);
      const room = Math.floor((swiper.slides.length - perView) / 2);

      swiper.params.loopAdditionalSlides = Math.max(0, Math.min(looped, room) - 1);
    }

    initSwiper() {
      if (!this.swiperEl || this.swiperEl.swiper) return;

      if (typeof Swiper === 'undefined') {
        this.swiperRetry = setTimeout(() => this.initSwiper(), 100);
        return;
      }

      const swiper = new Swiper(this.swiperEl, {
        slidesPerView: this.num('slidesMobile', 1.7),
        spaceBetween: this.num('gapMobile', 10),
        grabCursor: true,
        loop: this.loop,
        watchSlidesProgress: true,
        breakpoints: {
          768: {
            slidesPerView: this.num('slidesTablet', 2.2),
            spaceBetween: this.num('gapDesktop', 20),
          },
          1000: {
            slidesPerView: this.num('slidesDesktop', 3),
            spaceBetween: this.num('gapDesktop', 20),
          },
        },
      });

      ['slideChange', 'resize', 'breakpoint'].forEach((evt) =>
        swiper.on(evt, () => {
          this.fillOverflow(swiper);
          this.syncNav(swiper);
        })
      );
      this.fillOverflow(swiper);
      this.syncNav(swiper);

      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => swiper.slideNext());
      }
    }

    syncNav(swiper) {
      const activeSlide = swiper.slides[swiper.activeIndex];
      if (!activeSlide) return;

      if (this.counterEl) this.counterEl.textContent = Number(activeSlide.dataset.vcIndex) + 1;

      const activeCard = activeSlide.querySelector('[data-vc-card]');
      if (activeCard !== this.activeCard) {
        this.activeCard = activeCard;
        this.syncPlayback();
      }
    }

    /* ── Playback ───────────────────────────────────── */

    observeSelf() {
      if (!('IntersectionObserver' in window)) return;

      this.observer = new IntersectionObserver(
        (entries) => {
          this.inView = entries[0].isIntersecting;
          this.syncPlayback();
        },
        { threshold: 0.35 }
      );

      this.observer.observe(this);
    }

    syncPlayback() {
      const activeCard = this.activeCard;

      this.cards.forEach((card) => {
        const video = card.querySelector('video');
        if (!video) return;

        if (card !== activeCard) {
          video.pause();
          card.dataset.userPaused = 'false';
          this.setMuted(card, video, true);
        }
      });

      if (!activeCard || !this.inView || !this.autoplayAllowed) {
        if (activeCard && !this.inView) {
          const video = activeCard.querySelector('video');
          if (video) video.pause();
        }
        return;
      }

      if (activeCard.dataset.userPaused === 'true') return;

      const video = activeCard.querySelector('video');
      if (video) this.play(activeCard, video);
    }

    playOnly(card) {
      this.cards.forEach((other) => {
        if (other === card) return;
        const video = other.querySelector('video');
        if (!video) return;
        video.pause();
        this.setMuted(other, video, true);
      });

      const video = card.querySelector('video');
      if (video) this.play(card, video);
    }

    play(card, video) {
      const played = video.play();
      if (played && typeof played.then === 'function') {
        played.then(() => card.classList.add('is-playing')).catch(() => card.classList.remove('is-playing'));
      } else {
        card.classList.add('is-playing');
      }
    }

    setMuted(card, video, muted) {
      video.muted = muted;
      card.classList.toggle('is-unmuted', !muted);
      const muteBtn = card.querySelector('[data-vc-mute]');
      if (muteBtn) muteBtn.setAttribute('aria-label', muted ? 'Unmute video' : 'Mute video');
    }

    bindControls() {
      this.cards.forEach((card) => {
        const video = card.querySelector('video');
        if (!video) return;

        const playBtn = card.querySelector('[data-vc-play]');
        const muteBtn = card.querySelector('[data-vc-mute]');

        video.muted = true;

        video.addEventListener('play', () => {
          card.classList.add('is-playing');
          if (playBtn) playBtn.setAttribute('aria-label', 'Pause video');
        });

        video.addEventListener('pause', () => {
          card.classList.remove('is-playing');
          if (playBtn) playBtn.setAttribute('aria-label', 'Play video');
        });

        if (playBtn) {
          playBtn.addEventListener('click', () => {
            if (video.paused) {
              card.dataset.userPaused = 'false';
              this.playOnly(card);
            } else {
              card.dataset.userPaused = 'true';
              video.pause();
            }
          });
        }

        if (muteBtn) {
          muteBtn.addEventListener('click', () => {
            const unmuting = video.muted;
            if (unmuting) this.playOnly(card);
            this.setMuted(card, video, !unmuting);
          });
        }
      });
    }
  }

  customElements.define('videos-carousel', VideosCarousel);
}

document.addEventListener('shopify:block:select', (evt) => {
  const carousel = evt.target.closest('videos-carousel');
  if (!carousel) return;
  const swiperEl = carousel.querySelector('[data-vc-swiper]');
  if (!swiperEl || !swiperEl.swiper) return;
  const index = Number(evt.target.dataset.vcIndex);
  if (!Number.isNaN(index)) swiperEl.swiper.slideToLoop(index);
});
